/**
 * Ce înseamnă fiecare cifră de pe ecran: eticheta și precizarea „TVA · data · perimetrul”.
 *
 * Registrul cifrelor (03.11.2026, §D.2): fiecare cifră spune pe ecran dacă e cu sau fără TVA,
 * după ce dată se încadrează în perioadă și ce cuprinde. Etichetele stau aici, nu în fiecare
 * pagină, ca să le folosească la fel și web-ul, și verificarea lunară a cifrelor.
 *
 * `POLITICA_CIFRE` adună, într-un singur loc, alegerile care țin de politica contabilă a firmei.
 * Fiecare are alături alternativa și ce s-ar schimba dacă e aleasă.
 */
export const ETICHETE_CIFRE = {
    /** Tablou de bord vânzări — V1. */
    facturatClientiAgent: {
        eticheta: 'Facturat · clienții agentului',
        detaliu: 'fără TVA · după data facturii · facturi din EA',
    },
    /** Tablou de bord vânzări — V4. */
    incasat: { eticheta: 'Încasat', detaliu: 'cu TVA · după data extrasului' },
    /** Tablou de bord vânzări — V5. */
    deIncasat: { eticheta: 'De încasat', detaliu: 'cu TVA' },
    /** Profitabilitate — P1. */
    vanzariNete: {
        eticheta: 'Vânzări nete',
        detaliu: 'B2B · fără TVA · după reducerile de pe factură',
    },
    /** Profitabilitate — P2: prețul de intrare al mărfii vândute. */
    costMarfa: { eticheta: 'Costul mărfii', detaliu: 'fără TVA · prețul de intrare din EA' },
    /** Profitabilitate — P3: facturile primite de la clienți (marketing, bonus de volum, raft). */
    serviciiClienti: {
        eticheta: 'Servicii plătite clienților',
        detaliu: 'fără TVA · marketing, bonus de volum, raft',
    },
    /** Profitabilitate — P4 = P1 − P2 − P3 (până pe 04.11.2026: „Profit înainte de agenți”). */
    profitDinVanzari: {
        eticheta: 'Profit din vânzări',
        detaliu: 'fără TVA · vânzări − marfă − servicii plătite clienților',
    },
    /** Profitabilitate — P5 (până pe 04.11.2026: „Costuri agent”). */
    costuriEchipa: {
        eticheta: 'Costurile echipei de vânzări',
        detaliu: 'estimat · salarii, bonusuri, mașini, carburant',
    },
    /**
     * Profitabilitate — P4 − P5, cardul de sus (Mădălin, 03.11 și 04.11.2026; până atunci „Profit
     * după agenți”). Contribuție (OMFP 1802/2014: înainte de cheltuielile generale și de impozit), nu
     * profit net. Doar echipa de teren: fără manager, conturile naționale și clienții fără agent.
     */
    profitDupaEchipa: {
        eticheta: 'Profit rămas după echipă',
        detaliu: 'estimat · contribuție, nu profit net',
    },
    /**
     * Profitabilitate — P6: carburantul pe care procesele-verbale nu-l dau niciunui agent (masina la
     * sediu, la un om care nu e agent, sau fara PV dupa 30.11.2026). Se scade din „Total firmă"
     * (nu e al niciunui agent, deci nici al echipei), ca acesta sa nu para umflat (Mădălin, 04.11.2026).
     */
    costuriFlotaNealocate: {
        eticheta: 'Costuri flotă nealocate',
        detaliu: 'carburant fără agent, după procesele-verbale',
    },
    /** Discounturi acordate — D1 (doar B2B). */
    vanzariB2BInainteDeReduceri: {
        eticheta: 'Vânzări produse înainte de reduceri',
        detaliu: 'fără TVA',
    },
    /** Acasă — V14. */
    valoareComenzi: {
        eticheta: 'Valoarea comenzilor',
        detaliu: 'cu TVA · comenzi, nu facturi',
    },
    /** Comenzi — V17: fără ciorne, respinse și anulate. */
    valoareComenziAprobate: {
        eticheta: 'Valoare comenzi aprobate',
        detaliu: 'cu TVA · după data comenzii · fără ciorne, respinse, anulate · comenzi, nu facturi',
    },
    /** Shopify — Venit produse. */
    venitProduseShopify: {
        eticheta: 'Venit produse',
        detaliu: 'cu TVA · fără transport · după data comenzii',
    },
    /** Sell-Out — SO3. */
    sellOutLei: {
        eticheta: 'Sell-out (lei)',
        detaliu: 'estimat · la prețul mediu de vânzare',
    },
};
/** „Sell-in 2026” — anul curent, nu unul scris în cod. */
export function etichetaSellIn(an = new Date().getFullYear()) {
    return `Sell-in ${an}`;
}
/**
 * Stările comenzii CRM care intră în „Valoare comenzi aprobate” (V14 pe Acasă, V17 pe Comenzi).
 * Pe dinafară: `ciorna`, `asteapta_aprobare`, `respinsa`, `anulata` — nu sunt comenzi hotărâte.
 * `eroare_ea` rămâne: comanda e aprobată, doar trimiterea în EA a eșuat.
 */
export const STARI_COMANDA_APROBATA = ['aprobata', 'trimisa_ea', 'eroare_ea'];
export function esteComandaAprobata(status) {
    return STARI_COMANDA_APROBATA.includes(status);
}
/**
 * Valoarea comenzilor aprobate, cu TVA. Lista de pe ecran rămâne completă; doar totalul afișat
 * lasă pe dinafară ciornele, cele în așteptare, respinse și anulate.
 */
export function valoareComenziAprobate(comenzi) {
    let numar = 0;
    let valoare = 0;
    for (const c of comenzi) {
        if (!esteComandaAprobata(c.status))
            continue;
        numar += 1;
        valoare += Number(c.totalWithVat);
    }
    return { numar, valoare: Math.round(valoare * 100) / 100 };
}
/**
 * Deciziile de politică contabilă din care ies cifrele, cu alternativa fiecăreia. Cele marcate
 * „CONFIRMAT” sunt hotărâte; restul sunt de confirmat de Alexandru.
 */
export const POLITICA_CIFRE = {
    /**
     * Articolele EA prin care se facturează reducerile B2B, cu tipul lor pe „Discounturi acordate”.
     * Toate intră în costul promoțiilor B2B: „Total reduceri”, „% discount” și campanii.
     *
     * MK131 / MK128 („Paid Advertising” facturat cu minus): CONFIRMAT de Alexandru pe 01.11.2026 —
     * intră în costul promoțiilor, ca tip separat „publicitate la client”
     * (docs/superpowers/specs/2026-11-01-discounturi-acordate-design.md, §8 „Confirmate”).
     *
     * Alternativa, neaplicată (registrul cifrelor, O7): dacă sunt servicii de publicitate cumpărate
     * de la client, contabil sunt cheltuieli (clasa 6), nu 709 — s-ar raporta separat, în afara
     * reducerilor și a procentului. Efect: „Total reduceri” și „% discount” B2B ar scădea cu suma
     * MK, iar campaniile și-ar pierde facturile MK.
     */
    articoleReducereB2B: {
        RC1: 'comerciala',
        redcamp: 'campanie',
        MK131: 'publicitate',
        MK128: 'publicitate',
    },
    /**
     * Ce arată „Facturat” pe Tabloul de bord vânzări.
     *
     * Ales: `facturat_clienti_agent` — cifra de azi: facturile din EA (fără TVA, după data
     * facturii) ale clienților din portofoliul agentului, doar etichetată corect.
     *
     * Alternativa: `cifra_afaceri_contabila` — cifra de afaceri netă din nota contabilă EA
     * (701–708 − 709). Efect: s-ar potrivi cu balanța, dar n-ar mai putea fi împărțită pe agent
     * și pe client fără o alocare a lui 709. Nu e implementată.
     */
    vanzariTablouDeBord: 'facturat_clienti_agent',
};
//# sourceMappingURL=definitiiCifre.js.map